import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 标题区块样式
const HeaderSection = styled('section', {
  textAlign: 'center',
  marginBottom: '2rem'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '$textPrimary'
});

// 副标题样式
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 功能区块样式
const FeatureSection = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4rem',
  marginBottom: '3rem'
});

// 功能项样式
const FeatureItem = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '3rem',
  
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: '2rem'
  },
  
  variants: {
    reverse: {
      true: {
        flexDirection: 'row-reverse',
        
        '@media (max-width: 768px)': {
          flexDirection: 'column'
        }
      }
    }
  }
});

// 功能图片容器样式
const FeatureImageContainer = styled('div', {
  flex: '1',
  height: '400px',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  
  '@media (max-width: 768px)': {
    width: '100%',
    height: '300px'
  }
});

// 功能图片样式
const FeatureImage = styled('div', {
  width: '100%',
  height: '100%',
  background: 'center/cover no-repeat',
  transition: 'transform 0.5s ease',
  
  '&:hover': {
    transform: 'scale(1.05)'
  }
});

// 功能内容容器样式
const FeatureContent = styled('div', {
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem'
});

// 功能标题样式
const FeatureTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '$textPrimary',
  position: 'relative',
  paddingBottom: '0.75rem',
  
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '60px',
    height: '3px',
    background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)'
  }
});

// 功能描述样式
const FeatureDescription = styled('p', {
  fontSize: '1.1rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 功能列表样式
const FeatureList = styled('ul', {
  listStyle: 'none',
  padding: 0,
  margin: '0',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 功能列表项样式
const FeatureListItem = styled('li', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.75rem',
  fontSize: '1.05rem',
  color: '$textSecondary'
});

// 功能列表项图标样式
const FeatureListItemIcon = styled('span', {
  color: '$accentPrimary',
  fontSize: '1.25rem',
  lineHeight: 1
});

// 最终行动区块样式
const CtaSection = styled('section', {
  background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)',
  padding: '3rem 2rem',
  borderRadius: '16px',
  textAlign: 'center',
  color: 'white'
});

// 最终行动标题样式
const CtaTitle = styled('h2', {
  fontSize: '2.25rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem'
});

// 最终行动描述样式
const CtaDescription = styled('p', {
  fontSize: '1.1rem',
  maxWidth: '700px',
  margin: '0 auto 2rem',
  lineHeight: 1.6
});

// 按钮样式
const Button = styled('button', {
  padding: '0.875rem 2rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  background: 'white',
  color: '$accentPrimary',
  border: 'none',
  
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 15px rgba(0, 0, 0, 0.1)'
  }
});

/**
 * Kipa钱包功能页面组件
 * 详细介绍Kipa钱包的各项功能
 */
const KipaWalletFeatures: React.FC = () => {
  const { t } = useTranslation();
  
  // 钱包功能数据
  const walletFeatures = [
    {
      title: t('kipaWallet.features.multiChain.title', '多链支持'),
      description: t('kipaWallet.features.multiChain.description', 'Kipa钱包支持包括比特币、以太坊、Binance智能链、Solana等在内的多个区块链网络，让您能够在一个钱包应用中管理所有加密资产，无需安装多个钱包。'),
      image: '/assets/kipa-wallet-multichain.jpg',
      features: [
        t('kipaWallet.features.multiChain.feature1', '支持20+主流区块链网络'),
        t('kipaWallet.features.multiChain.feature2', '一键切换不同网络'),
        t('kipaWallet.features.multiChain.feature3', '自动识别代币类型'),
        t('kipaWallet.features.multiChain.feature4', '支持跨链资产查看')
      ]
    },
    {
      title: t('kipaWallet.features.security.title', '军事级安全防护'),
      description: t('kipaWallet.features.security.detailedDescription', 'Kipa钱包将安全放在首位，采用业界领先的加密技术和安全协议保护您的资产。您的私钥和助记词完全由您自己控制，我们无法访问。强大的安全措施确保即使设备丢失，您的资产也不会受到威胁。'),
      image: '/assets/kipa-wallet-security.jpg',
      features: [
        t('kipaWallet.features.security.feature1', '本地加密存储私钥'),
        t('kipaWallet.features.security.feature2', '多重生物识别验证'),
        t('kipaWallet.features.security.feature3', '交易确认机制'),
        t('kipaWallet.features.security.feature4', '可自定义安全级别')
      ],
      reverse: true
    },
    {
      title: t('kipaWallet.features.exchange.title', '内置交换功能'),
      description: t('kipaWallet.features.exchange.detailedDescription', '无需离开钱包应用，即可完成不同加密货币之间的兑换。Kipa钱包内置DEX聚合器，自动搜索多个去中心化交易所，为您提供最优惠的汇率和最低的手续费，让交易变得简单高效。'),
      image: '/assets/kipa-wallet-exchange.jpg',
      features: [
        t('kipaWallet.features.exchange.feature1', '支持上千种代币交换'),
        t('kipaWallet.features.exchange.feature2', '智能路由获取最佳价格'),
        t('kipaWallet.features.exchange.feature3', '实时价格行情'),
        t('kipaWallet.features.exchange.feature4', '交易历史记录和分析')
      ]
    },
    {
      title: t('kipaWallet.features.staking.title', '一键质押'),
      description: t('kipaWallet.features.staking.detailedDescription', 'Kipa钱包让您轻松参与PoS区块链网络的质押，只需几次点击即可开始赚取被动收益。我们提供多种质押选项，您可以根据自己的偏好选择不同的验证人和质押期限，最大化您的加密资产收益。'),
      image: '/assets/kipa-wallet-staking.jpg',
      features: [
        t('kipaWallet.features.staking.feature1', '支持多种PoS代币质押'),
        t('kipaWallet.features.staking.feature2', '验证人数据分析'),
        t('kipaWallet.features.staking.feature3', '收益计算器'),
        t('kipaWallet.features.staking.feature4', '自动复投选项')
      ],
      reverse: true
    },
    {
      title: t('kipaWallet.features.dapps.title', 'DApp浏览器'),
      description: t('kipaWallet.features.dapps.detailedDescription', 'Kipa钱包内置DApp浏览器，让您直接访问各种去中心化应用，包括DEX、借贷平台、NFT市场和游戏等。无需切换应用，即可在Web3世界中畅游，享受完整的去中心化生态系统。'),
      image: '/assets/kipa-wallet-dapps.jpg',
      features: [
        t('kipaWallet.features.dapps.feature1', '精选DApp推荐'),
        t('kipaWallet.features.dapps.feature2', '一键连接钱包'),
        t('kipaWallet.features.dapps.feature3', '交易签名确认'),
        t('kipaWallet.features.dapps.feature4', '收藏夹功能')
      ]
    }
  ];
  
  return (
    <PageContainer>
      <HeaderSection>
        <Title>{t('kipaWallet.featuresPage.title', 'Kipa钱包功能')}</Title>
        <Subtitle>
          {t('kipaWallet.featuresPage.subtitle', '探索Kipa钱包强大的功能，为您的数字资产管理提供全方位解决方案')}
        </Subtitle>
      </HeaderSection>
      
      <FeatureSection>
        {walletFeatures.map((feature, index) => (
          <FeatureItem key={index} reverse={feature.reverse}>
            <FeatureImageContainer>
              <FeatureImage style={{ background: `url(${feature.image}) center/cover no-repeat` }} />
            </FeatureImageContainer>
            
            <FeatureContent>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
              
              <FeatureList>
                {feature.features.map((item, i) => (
                  <FeatureListItem key={i}>
                    <FeatureListItemIcon>✓</FeatureListItemIcon>
                    {item}
                  </FeatureListItem>
                ))}
              </FeatureList>
            </FeatureContent>
          </FeatureItem>
        ))}
      </FeatureSection>
      
      <CtaSection>
        <CtaTitle>{t('kipaWallet.featuresPage.cta.title', '准备好体验Kipa钱包了吗？')}</CtaTitle>
        <CtaDescription>
          {t('kipaWallet.featuresPage.cta.description', '立即下载Kipa钱包，开始您的加密资产管理之旅。安全、便捷、功能丰富，为您的数字资产保驾护航。')}
        </CtaDescription>
        <Button>{t('kipaWallet.featuresPage.cta.download', '立即下载')}</Button>
      </CtaSection>
    </PageContainer>
  );
};

export default KipaWalletFeatures; 